import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import WebmasterSidebar from "../../components/WebmasterSidebar";
import { useAuth } from "../../context/AuthProvider";
import API_BASE_URL from "../../config/apiConfig";
import back from "../../assets/images/icon-back.svg";
import hide from "../../assets/images/hide.svg";
import show from "../../assets/images/show.svg";
import Swal from "sweetalert2";

interface TherapistCategory {
  key: string;
  value: string;
  priority: string;
  color: string;
}

const EditTherapist: React.FC = () => {
  const { token } = useAuth(); 
  const { therapistId } = useParams<{ therapistId: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    password: "",
    therapist_category: "",
    order_in_category: "" as string | number,
  });

  const [showPasswordField, setShowPasswordField] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [categories, setCategories] = useState<TherapistCategory[]>([]);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.onmouseenter = Swal.stopTimer;
      toast.onmouseleave = Swal.resumeTimer;
    },
  });

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) return "Parola trebuie să aibă cel puțin 8 caractere.";
    if (!/[A-Z]/.test(password)) return "Parola trebuie să conțină cel puțin o literă mare.";
    if (!/[a-z]/.test(password)) return "Parola trebuie să conțină cel puțin o literă mică.";
    if (!/\d/.test(password)) return "Parola trebuie să conțină cel puțin un număr.";
    if (!/[!@#$%^&*(),.?\":{}|<>]/.test(password)) return "Parola trebuie să conțină cel puțin un caracter special.";
    return null;
  };

  useEffect(() => {
    const fetchTherapist = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/users/therapist/${therapistId}`);
        if (!response.ok) throw new Error("Terapeutul nu a fost găsit.");
        const data = await response.json();
        setFormData({
          username: data.username,
          email: data.email,
          firstName: data.firstname,
          lastName: data.lastname,
          phone: data.phone || "",
          password: "",
          therapist_category: data.therapist_category || "",
          order_in_category: typeof data.order_in_category === "number" ? data.order_in_category : "",
        });
      } catch (err: any) {
        Toast.fire({ icon: "error", title: err.message });
      }
    };

    fetchTherapist();
  }, [therapistId]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/types/therapist-categories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Nu s-au putut prelua categoriile.");
        const data = await response.json();
        setCategories(data.data || []);
      } catch (error) {
        Toast.fire({ icon: "error", title: "Eroare la încărcarea categoriilor." });
      }
    };
    if (token) fetchCategories();
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password) {
      const passwordError = validatePassword(formData.password);
      if (passwordError) {
        Toast.fire({ icon: "error", title: passwordError });
        return;
      }
    }

    try {
      const payload: any = {
        username: formData.username,
        email: formData.email,
        firstname: formData.firstName,
        lastname: formData.lastName,
        phone: formData.phone,
        password: formData.password || null,
        therapist_category: formData.therapist_category,
      };

      if (formData.order_in_category !== "" && formData.order_in_category !== null) {
        payload.order_in_category = Number(formData.order_in_category);
      }
      

      const response = await fetch(`${API_BASE_URL}/users/therapist/${therapistId}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const { detail } = await response.json();
        throw new Error(detail || "A apărut o eroare.");
      }

      Toast.fire({ icon: "success", title: "Terapeut actualizat cu succes!" });
      setTimeout(() => navigate("/webmaster/therapists"), 2000);
    } catch (err: any) {
      Toast.fire({ icon: "error", title: err.message });
    }
  };

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <WebmasterSidebar onCollapseChange={(collapsed) => setIsCollapsed(collapsed)} />
      <div className="page-content">
        <h1 className="page-title">Editează Terapeut</h1>
        <div className="table-top">
          <div className="page-return">
            <button className="button-return" onClick={() => navigate(-1)}>
              <img src={back} alt="Înapoi" className="back-icon" /> Înapoi
            </button>
          </div>
        </div>
        <div className="form-container">
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <label>Username:</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                placeholder="Username"
              />
            </div>
            <div className="form-row">
              <label>Email:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Adresa de e-mail"
              />
            </div>
            <div className="form-row">
              <label>Nume:</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                placeholder="Nume"
              />
            </div>
            <div className="form-row">
              <label>Prenume:</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                placeholder="Prenume"
              />
            </div>
            <div className="form-row">
              <label>Telefon:</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Telefon"
              />
            </div>

            <div className="form-row">
              <label>Categorie terapeut:</label>
              <select
                name="therapist_category"
                value={formData.therapist_category}
                onChange={handleChange}
                required
              >
                <option value="">Selectează tip încadrare</option>
                {categories.map((cat) => (
                  <option key={cat.key} value={cat.key}>
                    {cat.value}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label>Ordine în categorie (opțional):</label>
              <input
                type="number"
                name="order_in_category"
                min={0}
                value={formData.order_in_category}
                onChange={handleChange}
                placeholder="Ordine în categorie"
              />
            </div>

            <div className="form-row edit-password">
              <button
                type="button"
                onClick={() => setShowPasswordField(!showPasswordField)}
              >
                {showPasswordField ? "Ascunde câmpul de parolă" : "Modifică parola"}
              </button>
            </div>

            {showPasswordField && (
              <div className="form-row password-field">
                <label>Parolă:</label>
                <div>
                  <input
                    type={isPasswordVisible ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Parola"
                  />
                  <button
                    type="button"
                    onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  >
                    {isPasswordVisible ? (
                      <img className="hide-password" src={hide} alt="Ascunde" />
                    ) : (
                      <img className="show-password" src={show} alt="Arată" />
                    )}
                  </button>
                </div>
              </div>
            )}

            <div className="form-row form-actions">
              <button className="cancel-btn" onClick={() => navigate(-1)} type="button">
                Anulează
              </button>
              <button className="primary-btn" type="submit">
                Salvează
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditTherapist;
